// ============================================================================
// useFacilityScope — the ONE definition of "which facilities am I looking at".
//
// CW-A (2026-08-04). Before this hook there were two different answers to that
// question: Account Management's list/map hard-filtered to the AM's own
// facilities, while /map read useMapData() unscoped and always showed all 231.
// Both surfaces now consume this hook so they cannot diverge again.
//
// Model (Ben's decision, 2026-08-04):
//   * scope='mine' → AM-of-record set UNION the user's ACTIVE route
//     assignments (the AFB-0033 union — a facility on your route but not yet
//     reassigned to you is still yours to work).
//   * scope='all'  → no scope filter at all. RLS already permits org-wide
//     read (am_read_all, tcs_employee_read_all), so this is a client concern.
//   * DEFAULT: 'mine' when the user has any assigned facilities, otherwise
//     'all'. This is the guarantee that a new hire — or a superuser, or
//     Jose in finance — never lands on a blank screen.
//   * The choice is persisted to localStorage PER USER, so switching accounts
//     on a shared machine does not inherit the other person's scope.
//
// When the user has zero assigned facilities, 'mine' is not offered at all
// (`canScopeToMine === false`) and the effective scope is forced to 'all'.
// ============================================================================

import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../auth';
import { errorMessage } from '../errors';

export type FacilityScope = 'mine' | 'all';

const STORAGE_PREFIX = 'tcs.facilityScope.';

function storageKey(employeeId: string | null | undefined): string | null {
  return employeeId ? `${STORAGE_PREFIX}${employeeId}` : null;
}

function readStored(employeeId: string | null | undefined): FacilityScope | null {
  const key = storageKey(employeeId);
  if (!key) return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw === 'mine' || raw === 'all' ? raw : null;
  } catch {
    return null;
  }
}

function writeStored(employeeId: string | null | undefined, scope: FacilityScope): void {
  const key = storageKey(employeeId);
  if (!key) return;
  try {
    window.localStorage.setItem(key, scope);
  } catch {
    /* private browsing / quota — scope just falls back to the default */
  }
}

/**
 * Default resolution, extracted pure so it can be tested without a renderer.
 *
 * The rule (Ben's decision, 2026-08-04): assigned if any, else ALL. A stored
 * preference wins ONLY when it is actually available — 'mine' with an empty
 * assigned set is not, which is the blank-screen guarantee for a new hire.
 */
export function resolveScope(args: {
  stored: FacilityScope | null;
  canScopeToMine: boolean;
  resolved: boolean;
}): FacilityScope {
  const { stored, canScopeToMine, resolved } = args;
  if (!resolved) return stored ?? 'mine';
  if (!canScopeToMine) return 'all';
  return stored ?? 'mine';
}

export interface FacilityScopeState {
  /** The scope actually in effect. Forced to 'all' when the user has none. */
  scope: FacilityScope;
  /** Persist and apply a new scope. No-op for 'mine' when nothing is assigned. */
  setScope: (next: FacilityScope) => void;
  /** AM-of-record ∪ active route assignments. Empty until `resolved`. */
  myFacilityIds: string[];
  /** False when the user has zero assigned facilities — hide/disable 'mine'. */
  canScopeToMine: boolean;
  /** True once the assigned-set query has settled (success OR failure). */
  resolved: boolean;
  /** Non-null when the assigned-set lookup failed; scope degrades to 'all'. */
  error: string | null;
}

export function useFacilityScope(): FacilityScopeState {
  const { employee } = useAuth();
  const eid = employee?.employee_id ?? null;

  const [myFacilityIds, setMyFacilityIds] = useState<string[]>([]);
  const [resolved, setResolved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stored, setStored] = useState<FacilityScope | null>(() => readStored(eid));

  // Re-read the persisted choice when the signed-in user changes.
  useEffect(() => {
    setStored(readStored(eid));
  }, [eid]);

  // Resolve the assigned set: AM-of-record ∪ active route assignments.
  useEffect(() => {
    let cancelled = false;
    if (!eid) {
      setMyFacilityIds([]);
      setResolved(true);
      return;
    }
    setResolved(false);
    (async () => {
      const [ownRes, routeRes] = await Promise.all([
        supabase.from('facilities').select('facility_id').eq('account_manager_eid', eid),
        supabase
          .from('am_route_assignments')
          .select('facility_id')
          .eq('am_eid', eid)
          .eq('active', true)
          .not('facility_id', 'is', null),
      ]);
      if (cancelled) return;
      // PostgrestError is NOT an Error instance — use errorMessage, never
      // `instanceof Error` (see src/shared/errors.ts).
      const firstErr = ownRes.error ?? routeRes.error;
      if (firstErr) {
        setError(errorMessage(firstErr, 'Failed to resolve your assigned facilities.'));
        setMyFacilityIds([]);
        setResolved(true);
        return;
      }
      setError(null);
      const ids = new Set<string>();
      for (const r of ownRes.data ?? []) if (r.facility_id) ids.add(r.facility_id);
      for (const r of routeRes.data ?? []) if (r.facility_id) ids.add(r.facility_id);
      setMyFacilityIds(Array.from(ids));
      setResolved(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [eid]);

  const canScopeToMine = myFacilityIds.length > 0;

  // Default resolution. The assigned count is the input, exactly as specified:
  // scope to 'mine' when there is something to scope to, otherwise 'all'.
  const scope: FacilityScope = useMemo(
    () => resolveScope({ stored, canScopeToMine, resolved }),
    [resolved, stored, canScopeToMine],
  );

  const setScope = useCallback(
    (next: FacilityScope) => {
      if (next === 'mine' && !canScopeToMine) return;
      setStored(next);
      writeStored(eid, next);
    },
    [eid, canScopeToMine],
  );

  return { scope, setScope, myFacilityIds, canScopeToMine, resolved, error };
}
